(() => {
  'use strict';
  if (/\/products\/epure-oval(?:\/|$)/.test(window.location?.pathname || '') || window.__CorsailleIntentVideosReady) return;
  window.__CorsailleIntentVideosReady = true;
  const selector = '.cs-home-portal__video, [data-cs-intent-video]';
  const controllers = new Map();
  const hydrate = (video, state) => {
    if (state.hydrated || document.hidden) return;
    state.hydrated = true;
    if (video.dataset.poster && video.getAttribute('poster') !== video.dataset.poster) video.poster = video.dataset.poster;
    let changed = false;
    video.preload = 'auto';
    video.querySelectorAll('source[data-src]').forEach(source => {
      if (source.dataset.src && source.getAttribute('src') !== source.dataset.src) { source.src = source.dataset.src; changed = true; }
    });
    if (video.dataset.src && video.getAttribute('src') !== video.dataset.src) { video.src = video.dataset.src; changed = true; }
    if (changed) video.load();
  };
  const setPlayback = (video, state) => {
    if (!state.visible || document.hidden || state.disposed) {
      if (!video.paused) video.pause();
      return;
    }
    hydrate(video, state);
    if (state.blocked || state.pending || !video.paused) return;
    state.pending = true;
    let playback;
    try { playback = video.play(); }
    catch { state.blocked = true; state.pending = false; video.dataset.csRotationState = 'browser-blocked'; return; }
    Promise.resolve(playback).then(() => {
      if (state.disposed || !state.visible || document.hidden) video.pause();
      else video.dataset.csRotationState = 'playing';
    }).catch(error => {
      if (error?.name !== 'AbortError') {
        state.blocked = true;
        video.dataset.csRotationState = 'browser-blocked';
      }
    }).finally(() => { state.pending = false; });
  };
  const mount = video => {
    if (controllers.has(video)) return;
    const base = Number(video.dataset.csRotationBaseRate) || Number(video.defaultPlaybackRate) || 1;
    video.dataset.csRotationBaseRate = String(base);
    video.defaultPlaybackRate = video.playbackRate = base * 2;
    video.muted = video.defaultMuted = true;
    video.autoplay = false;
    video.loop = video.playsInline = true;
    video.controls = false;
    video.removeAttribute('controls'); video.removeAttribute('autoplay');
    ['loop', 'muted', 'playsinline'].forEach(name => video.setAttribute(name, ''));
    video.pause();
    const controller = new AbortController();
    const state = { visible: false, warm: false, hydrated: false, observer: null, warmObserver: null, controller, blocked: false, pending: false, recoveredPause: false, disposed: false, fallbackFrame: 0 };
    const sync = () => setPlayback(video, state);
    const refresh = () => {
      const rect = video.getBoundingClientRect(), height = window.innerHeight || document.documentElement.clientHeight;
      state.warm = rect.bottom > -600 && rect.top < height + 600;
      const intersection = Math.max(0, Math.min(rect.bottom, height) - Math.max(rect.top, 0));
      const wasVisible = state.visible;
      state.visible = rect.width > 0 && rect.height > 0 && intersection / rect.height >= .2;
      if (state.visible && !wasVisible) state.recoveredPause = false;
      if (state.warm) hydrate(video, state);
      sync();
    };
    const options = { signal: controller.signal };
    if ('IntersectionObserver' in window) {
      state.warmObserver = new IntersectionObserver(entries => {
        state.warm = entries[entries.length - 1].isIntersecting;
        if (state.warm) hydrate(video, state);
      }, { rootMargin: '600px 0px', threshold: 0 });
      state.observer = new IntersectionObserver(entries => {
        const entry = entries[entries.length - 1], wasVisible = state.visible;
        state.visible = Boolean(entry?.isIntersecting && entry.intersectionRatio >= .2);
        if (state.visible && !wasVisible) state.recoveredPause = false;
        sync();
      }, { threshold: [0, .2, .6] });
      state.warmObserver.observe(video); state.observer.observe(video);
    } else {
      const schedule = () => {
        if (!state.fallbackFrame) state.fallbackFrame = requestAnimationFrame(() => { state.fallbackFrame = 0; refresh(); });
      };
      window.addEventListener('scroll', schedule, { ...options, passive: true });
      window.addEventListener('resize', schedule, options);
      refresh();
    }
    video.addEventListener('loadedmetadata', () => { video.defaultPlaybackRate = video.playbackRate = base * 2; sync(); }, options);
    video.addEventListener('canplay', sync, options);
    video.addEventListener('ended', sync, options);
    video.addEventListener('pause', () => {
      if (!state.visible || document.hidden || state.blocked || state.recoveredPause || state.disposed) return;
      state.recoveredPause = true;
      window.setTimeout(sync, 0);
    }, options);
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) { state.recoveredPause = false; refresh(); }
      else sync();
    }, options);
    const recover = () => { if (state.blocked) { state.blocked = false; sync(); } };
    document.addEventListener('pointerdown', recover, { ...options, once: true, capture: true });
    document.addEventListener('keydown', recover, { ...options, once: true, capture: true });
    controllers.set(video, state);
  };
  const unmount = video => {
    const state = controllers.get(video);
    if (!state) return;
    state.disposed = true; state.visible = false;
    state.observer?.disconnect(); state.warmObserver?.disconnect();
    if (state.fallbackFrame) cancelAnimationFrame(state.fallbackFrame);
    state.controller.abort(); video.pause(); controllers.delete(video);
  };
  const mountWithin = (scope = document) => {
    if (scope?.matches?.(selector)) mount(scope);
    scope?.querySelectorAll?.(selector).forEach(mount);
  };
  const unmountWithin = scope => {
    if (scope?.matches?.(selector)) unmount(scope);
    scope?.querySelectorAll?.(selector).forEach(unmount);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mountWithin(), { once: true });
  else mountWithin();
  document.addEventListener('shopify:section:load', event => mountWithin(event.target));
  document.addEventListener('shopify:section:unload', event => unmountWithin(event.target));
  window.addEventListener('pagehide', () => [...controllers.keys()].forEach(unmount));
  window.addEventListener('pageshow', () => mountWithin());
})();
